/**
 * Bausteine der Artikelakte fuer Varianten und Sets:
 *
 *   - VariantAttributesEditor: Merkmale (Name/Wert) einer Variante bzw. die
 *     Merkmalsnamen am Elternartikel.
 *   - VariantsCard: Varianten eines Elternartikels mit Absprung und
 *     "Neue Variante" (legt den Artikel mit Vorbelegung aus dem Eltern an).
 *   - ComponentsCard: Stueckliste eines Sets mit Verfuegbarkeit, Einstand
 *     und Preisvorschlag; speichert getrennt vom Artikel.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { formatPrice, formatQuantity, variantAttributesText } from '@erp/shared';

import { ApiError, api } from '../api/client';
import type { Item, ItemComponent, ItemVariant, SetSummary } from '../api/types';
import { useToast } from './toast';
import { ItemPicker } from './ItemPicker';
import { Badge, EmptyState, NumericInput, TextInput } from './ui';

export function VariantAttributesEditor({
  value,
  onChange,
  namesOnly,
  disabled,
}: {
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  /** Elternartikel: nur die Merkmalsnamen, die Werte stehen an den Varianten. */
  namesOnly?: boolean;
  disabled?: boolean;
}) {
  // Reihenfolge und Zwischenzustand (leerer Name waehrend des Tippens) brauchen
  // eine Liste; das Objekt entsteht daraus beim Melden nach oben.
  const [rows, setRows] = useState<{ name: string; value: string }[]>(() =>
    Object.entries(value).map(([name, v]) => ({ name, value: v })),
  );
  useEffect(() => {
    const fromValue = Object.entries(value);
    const current = rows.filter((row) => row.name.trim());
    if (
      fromValue.length !== current.length ||
      fromValue.some(([name, v], i) => current[i]?.name !== name || current[i]?.value !== v)
    ) {
      setRows(fromValue.map(([name, v]) => ({ name, value: v })));
    }
    // Nur wenn der Wert von aussen wechselt (Laden, Vorbelegung).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function emit(next: { name: string; value: string }[]) {
    setRows(next);
    const result: Record<string, string> = {};
    for (const row of next) if (row.name.trim()) result[row.name.trim()] = row.value;
    onChange(result);
  }

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {rows.map((row, index) => (
        <div key={index} className="row" style={{ gap: 6, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <TextInput
              value={row.name}
              onChange={(name) => emit(rows.map((r, i) => (i === index ? { ...r, name } : r)))}
              placeholder="Merkmal, z. B. Farbe"
              disabled={disabled}
            />
          </div>
          {namesOnly ? null : (
            <div style={{ flex: 1 }}>
              <TextInput
                value={row.value}
                onChange={(v) => emit(rows.map((r, i) => (i === index ? { ...r, value: v } : r)))}
                placeholder="Wert, z. B. rot"
                disabled={disabled}
              />
            </div>
          )}
          {!disabled ? (
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              title="Merkmal entfernen"
              onClick={() => emit(rows.filter((_, i) => i !== index))}
            >
              ×
            </button>
          ) : null}
        </div>
      ))}
      {!disabled ? (
        <div>
          <button type="button" className="btn btn-sm" onClick={() => setRows([...rows, { name: '', value: '' }])}>
            Merkmal hinzufügen
          </button>
        </div>
      ) : null}
      {rows.length === 0 && disabled ? <span className="faint">Keine Merkmale</span> : null}
    </div>
  );
}

export function VariantsCard({
  parentId,
  variants,
  canWrite,
}: {
  parentId: string;
  variants: ItemVariant[];
  canWrite: boolean;
}) {
  const navigate = useNavigate();
  return (
    <div className="card">
      <div className="card-body">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0 }}>Varianten</h3>
            <div className="faint">
              {variants.length === 0 ? 'Noch keine Varianten' : `${variants.length} Variante${variants.length === 1 ? '' : 'n'}`}
              {' · '}Der Elternartikel selbst kommt nicht in den Beleg, gewählt wird immer die Variante.
            </div>
          </div>
          {canWrite ? (
            <button type="button" className="btn btn-primary" onClick={() => navigate(`/artikel/neu?variantOf=${parentId}`)}>
              Neue Variante
            </button>
          ) : null}
        </div>
      </div>
      {variants.length === 0 ? (
        <EmptyState title="Noch keine Varianten" hint={"Mit „Neue Variante“ entsteht ein Artikel mit den Vorgaben dieses Elternartikels."} />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="shrink">Nummer</th>
                <th>Bezeichnung</th>
                <th>Merkmale</th>
                <th className="shrink">EAN</th>
                <th className="numeric">VK netto</th>
                <th className="numeric">Bestand</th>
                <th className="shrink"></th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) => (
                <tr key={variant.id}>
                  <td className="mono">
                    <Link to={`/artikel/${variant.id}`}>{variant.number}</Link>
                  </td>
                  <td>{variant.name}</td>
                  <td className="muted">{variantAttributesText(variant.variantAttributes) || <span className="faint">–</span>}</td>
                  <td className="mono muted">{variant.gtin ?? ''}</td>
                  <td className="numeric">{formatPrice(variant.salesPrice)}</td>
                  <td className="numeric">
                    {variant.isStockItem ? (
                      <span
                        style={
                          variant.minStock > 0 && variant.stockQuantity < variant.minStock
                            ? { color: 'var(--warning)', fontWeight: 600 }
                            : undefined
                        }
                      >
                        {formatQuantity(variant.stockQuantity)}
                      </span>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td>{!variant.active ? <Badge kind="warning">Inaktiv</Badge> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type ComponentRow = { componentItemId: string; number: string; name: string; unit: string; quantity: number; note: string };

export function ComponentsCard({
  setId,
  components,
  summary,
  salesPrice,
  canWrite,
}: {
  setId: string;
  components: ItemComponent[];
  summary: SetSummary | null;
  /** Verkaufspreis des Sets (Zehntausendstel) fuer den Vergleich mit Einstand und Einzelsumme. */
  salesPrice: number;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [rows, setRows] = useState<ComponentRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setRows(
      components.map((c) => ({
        componentItemId: c.componentItemId,
        number: c.number,
        name: c.name,
        unit: c.unit,
        quantity: c.quantity,
        note: c.note ?? '',
      })),
    );
    setDirty(false);
    setErrors({});
  }, [components]);

  const save = useMutation({
    mutationFn: () =>
      api.put<{ components: ItemComponent[]; setSummary: SetSummary }>(`/api/items/${setId}/components`, {
        components: rows.map((row) => ({ componentItemId: row.componentItemId, quantity: row.quantity, note: row.note || null })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', setId] });
      toast.success('Stückliste gespeichert.');
      setDirty(false);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else toast.error('Die Stückliste konnte nicht gespeichert werden.');
    },
  });

  function edit(next: ComponentRow[]) {
    setRows(next);
    setDirty(true);
  }

  function add(item: Item) {
    if (rows.some((row) => row.componentItemId === item.id)) {
      toast.info(`${item.number} steht schon in der Stückliste.`);
      return;
    }
    edit([...rows, { componentItemId: item.id, number: item.number, name: item.name, unit: item.unit, quantity: 1000, note: '' }]);
  }

  const margin = summary && salesPrice > 0 ? ((salesPrice - summary.costSum) / salesPrice) * 100 : null;

  return (
    <div className="card">
      <div className="card-body">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <h3 style={{ margin: 0 }}>Stückliste</h3>
            <div className="faint">
              Menge je Set. Der Lieferschein bucht die Bestandteile aus, das Set selbst führt keinen Bestand.
            </div>
          </div>
          {canWrite ? (
            <button type="button" className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Wird gespeichert …' : 'Stückliste speichern'}
            </button>
          ) : null}
        </div>

        {summary ? (
          <div className="row" style={{ gap: 24, marginTop: 12, flexWrap: 'wrap' }}>
            <Stat
              label="Baubar"
              value={summary.buildable === null ? '—' : `${summary.buildable} ×`}
              hint={
                summary.limiting
                  ? `begrenzt durch ${summary.limiting.number} (${formatQuantity(summary.limiting.stockQuantity)} da, ${formatQuantity(summary.limiting.needed)} je Set)`
                  : 'kein bestandsgeführter Bestandteil'
              }
              warn={summary.buildable !== null && summary.buildable === 0}
            />
            <Stat label="Einstand je Set" value={`${formatPrice(summary.costSum)} €`} hint="Summe der gleitenden Einstandspreise" />
            <Stat label="Summe Einzelpreise" value={`${formatPrice(summary.priceSum)} €`} hint="Preisvorschlag; Setpreis darunter = Paketvorteil" />
            <Stat
              label="Rohertrag beim Setpreis"
              value={salesPrice > 0 ? `${formatPrice(salesPrice - summary.costSum)} €` : '—'}
              hint={margin !== null ? `${margin.toLocaleString('de-DE', { maximumFractionDigits: 1 })} % vom Setpreis` : 'Setpreis fehlt'}
              warn={margin !== null && margin < 0}
            />
          </div>
        ) : null}
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th className="shrink">Nummer</th>
              <th>Bestandteil</th>
              <th className="numeric" style={{ width: 140 }}>Menge je Set</th>
              <th className="shrink">Einheit</th>
              <th>Hinweis</th>
              {canWrite ? <th className="shrink"></th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const error = errors[`components.${index}`] ?? errors[`components.${index}.quantity`];
              return (
                <tr key={row.componentItemId}>
                  <td className="mono">
                    <Link to={`/artikel/${row.componentItemId}`}>{row.number}</Link>
                  </td>
                  <td>
                    {row.name}
                    {error ? <div style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</div> : null}
                  </td>
                  <td className="numeric">
                    <NumericInput
                      kind="quantity"
                      value={row.quantity}
                      onChange={(quantity) => edit(rows.map((r, i) => (i === index ? { ...r, quantity } : r)))}
                      disabled={!canWrite}
                      invalid={Boolean(errors[`components.${index}.quantity`])}
                    />
                  </td>
                  <td className="faint">{row.unit}</td>
                  <td>
                    <TextInput
                      value={row.note}
                      onChange={(note) => edit(rows.map((r, i) => (i === index ? { ...r, note } : r)))}
                      placeholder="z. B. Schutzausrüstung"
                      disabled={!canWrite}
                    />
                  </td>
                  {canWrite ? (
                    <td>
                      <button type="button" className="btn btn-quiet btn-sm" title="Entfernen" onClick={() => edit(rows.filter((_, i) => i !== index))}>
                        ×
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
            {canWrite ? (
              <tr>
                <td colSpan={6}>
                  <div style={{ maxWidth: 480 }}>
                    <ItemPicker value="" onSelect={add} placeholder="Bestandteil hinzufügen: Artikelnummer oder Bezeichnung …" />
                  </div>
                </td>
              </tr>
            ) : null}
            {rows.length === 0 && !canWrite ? (
              <tr>
                <td colSpan={5} className="faint">
                  Noch keine Bestandteile
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, warn }: { label: string; value: string; hint?: string; warn?: boolean }) {
  return (
    <div>
      <div className="faint" style={{ fontSize: 12 }}>
        {label}
      </div>
      <div style={{ fontWeight: 600, fontSize: 16, color: warn ? 'var(--warning)' : undefined }}>{value}</div>
      {hint ? (
        <div className="faint" style={{ fontSize: 12 }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}
